import { VDBText } from "@components/atoms";
import Tooltip from "@components/atoms/Tooltip";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import BulkListModal from "@components/molecules/UnifiedBulkList/BulkListModal";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import BulkListSummary from "@components/organisms/BulkListSummary";
import Routes from "@navigations/Routes";
import { useNavigation } from "@react-navigation/native";
import { navigate } from "@navigations/utils/RootNavigation";
import { useBulkRequestSlice } from "@redux/slices/bulkRequestSlice";
import { useOrderCartSlice } from "@redux/slices/orderCartSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ProductCategory } from "@utils/constants";
import { VDBIcons } from "@utils/VDBIcons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceEventEmitter, TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useNavigationHistory } from "../../hooks/useNavigationHistory";
import { useDispatch, useSelector } from "react-redux";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { BuilderAlertManager } from "@scenes/jewelry-builder/components/BuilderAlertManager";
import BuilderAlertModal from "@scenes/jewelry-builder/components/BuilderAlertModal";
import { resetFiltersPayload } from "@redux/slices/searchFilters";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { StoneType } from "./Utils";

const BulkListHeaderButton = ({ color }: any) => {
  const theme = useTheme();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { clearRouteHistory } = useNavigationHistory();
  const dispatch = useDispatch();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const navigation = useNavigation();
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState(false);
  const [openBulkListModal, setOpenBulkListModal] = useState(false);
  const userInfo = useUserInfo();
  const typography = useTypographyStore();
  const [bulkTotalCount, setBulkTotalCount] = useState(0);
  const bulkRequestSlice = useBulkRequestSlice();
  const totalProductCount = bulkRequestSlice.totalProductCount();
  const bulkItemData = bulkRequestSlice.bulkItemData;
  const [singleProductType, setSingleProductType] = useState("");
  const orderCartSlice = useOrderCartSlice();
  const showToastWhenBulkListIsEmpty = () => {
    showToast(
      undefined,
      t("bulkRequest.itemsNotAvailableInCart.message.text"),
      ToastTypes.INFO,
      true,
      true
    );
  };

  const handleTooltipToggle = () => {
    if (totalProductCount > 1) {
      setOpenMenu((prev) => !prev);
    } else if (totalProductCount === 0) {
      showToastWhenBulkListIsEmpty();
    } else if (totalProductCount === 1) {
      navigate(Routes.BulkRequestNavigator, {
        screen: Routes.BulkRequestScreen,
        params: {
          productType: singleProductType,
          stone_type: bulkItemData?.[0]?.product_sub_type,
        },
      });
    }
  };

  const handleMobileEvent = () => {
    if (totalProductCount > 1) {
      setOpenBulkListModal(true);
    } else if (totalProductCount === 0) {
      showToastWhenBulkListIsEmpty();
    } else if (totalProductCount === 1) {
      navigate(Routes.BulkRequestNavigator, {
        screen: Routes.BulkRequestScreen,
        params: {
          productType: singleProductType,
          stone_type: bulkItemData?.[0]?.product_sub_type,
        },
      });
    }
  };

  const navigateToBulkRequestScreen = (
    product_type: any,
    stone_type?: StoneType
  ) => {
    setOpenMenu(false);
    navigate(Routes.BulkRequestNavigator, {
      screen: Routes.BulkRequestScreen,
      params: {
        productType: product_type,
        stone_type: stone_type,
      },
    });
    if (enableJewelryBuilder) {
      dispatch(resetFiltersPayload());
      if (history.length > 1) {
        navigation.pop(history.length);
      }
      clearRouteHistory();
    }
  };

  useEffect(() => {
    const allowedStockTypes = userInfo?.getAllowedStockTypes();
    const bulkItemProductArray = bulkItemData.filter((_item) =>
      allowedStockTypes?.includes(_item.product_type)
    );

    if (bulkItemProductArray?.length === 1) {
      setSingleProductType(bulkItemProductArray[0].product_type);
    }

    const _bulkTotalCount = bulkItemData.reduce((total: number, curr: any) => {
      const count = total + Number(curr.total_count ?? 0);
      return count;
    }, 0);

    if (userInfo.getToken()) {
      setBulkTotalCount(
        userInfo.getFeatureSettings().enable_order_cart ||
          userInfo.getFeatureSettings().shopping_cart
          ? orderCartSlice?.orderCartData?.total_cart_items
          : _bulkTotalCount
      );
    }
  }, [
    bulkItemData,
    orderCartSlice?.orderCartData,
    userInfo?.user?.price_mode,
    userInfo.getToken(),
  ]);

  if (theme.isLargeScreen || userInfo.isIFrame()) {
    return null;
  }

  const renderCartIcon = (triggerProps: any) => {
    return (
      <TouchableOpacity
        {...(theme.isTablet ? triggerProps : {})}
        onPress={() => {
          if (userInfo.isGuest) {
            dispatch(setLoginModalType("default"));
          } else {
            const callback = () => {
              if (
                (userInfo.getFeatureSettings()?.enable_order_cart &&
                  userInfo?.user?.price_mode !== PriceModes.GREEN) ||
                (userInfo.getFeatureSettings()?.shopping_cart &&
                  userInfo?.user?.price_mode === PriceModes.ORANGE)
              ) {
                navigate(Routes.BulkRequestNavigator, {
                  screen: Routes.OrderCartScreen,
                });
              } else if (theme.isTablet) {
                handleTooltipToggle();
              } else if (theme.isMobile) {
                handleMobileEvent();
              }
            };
            if (enableJewelryBuilder && history.length > 2) {
              BuilderAlertManager.open(() => {
                callback();
              });
            } else {
              if (enableJewelryBuilder && totalProductCount === 1) {
                dispatch(resetFiltersPayload());
                if (history.length > 1) {
                  navigation.pop(history.length);
                }
                clearRouteHistory();
                // Reset builder context so a re-entered flow starts clean.
                DeviceEventEmitter.emit("CLEAR_JEWELRY_CONTEXT");
              }
              callback();
            }
          }
        }}
      >
        <VDBIcon
          color={theme[color] ?? "black"}
          size={24}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.BULK_LIST_SHOPPING_CART}
        />
        {!userInfo.isGuest && bulkTotalCount > 0 && (
          <BulkCountBadge>
            <VDBText type={typography["Minor-M"]} color={theme["cont-00"]}>
              {bulkTotalCount ?? 0}
            </VDBText>
          </BulkCountBadge>
        )}
      </TouchableOpacity>
    );
  };

  const cartToolTip = (
    <Tooltip
      showOnPress
      isOpen={openMenu}
      setIsOpen={setOpenMenu}
      showArrowIcon={false}
      trigger={(triggerProps) => renderCartIcon(triggerProps)}
      tooltipBackgroundColor={theme["over-01"]}
      popoverBackdropColor={"transparent"}
      popoverProps={{ size: "md" }}
      popoverContentProps={{ marginTop: 12 }}
      popoverBodyProps={{
        padding: 0,
        minWidth: 350,
        backgroundColor: theme["core-02"],
      }}
    >
      {totalProductCount > 0 && (
        <BulkListSummary navigateToBulkScreen={navigateToBulkRequestScreen} />
      )}
    </Tooltip>
  );

  const shouldShowCart =
    (userInfo?.getToken() &&
      userInfo?.showBulkRequestFeature(
        userInfo
          ?.getAllowedStockTypes()
          ?.find((mItem) =>
            userInfo?.user?.feature_settings?.allowed_bulk_request_types?.includes(
              mItem ?? ""
            )
          ) as ProductCategory,
        userInfo?.user?.price_mode
      )) ||
    (userInfo.getFeatureSettings()?.enable_order_cart &&
      userInfo?.user?.price_mode !== PriceModes.GREEN) ||
    (userInfo.getFeatureSettings()?.shopping_cart &&
      userInfo?.user?.price_mode === PriceModes.ORANGE);

  return shouldShowCart ? (
    theme.isTablet ? (
      cartToolTip
    ) : theme.isMobile ? (
      <>
        {renderCartIcon(null)}
        <BulkListModal
          isOpen={openBulkListModal}
          onClose={() => {
            setOpenBulkListModal(false);
          }}
        />
        <BuilderAlertModal />
      </>
    ) : null
  ) : null;
};

const BulkCountBadge = styled.View`
  height: 22px;
  width: 27px;
  background: ${({ theme }) => theme["error-01"]};
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  position: absolute;
  right: -12px;
  top: -12px;
`;

export default React.memo(BulkListHeaderButton, (prevProps, nextProps) => {
  // Component should re-render when color changes
  // Note: Price mode changes are handled via hooks inside the component
  return prevProps.color === nextProps.color;
});
